/**
 * **The member app's confirmation card** — `Web App.dc.html` §3.1's `resDone`: a centred card at
 * `--radius-xl` over `--shadow-sm`, a 72px round `--success-subtle` disc with the check in
 * `--success`, the heading at `--text-3xl`, one sentence held to 420px, and one solid brand action.
 *
 * It was the reservation's alone (TW 44132582). Brad chose it on 2026-10-01 as the layout for every
 * member confirmation (TW 44261258, "Go with Option A"), so it lives here rather than in
 * `event-orders`, and the screens that confirm something draw this rather than a copy of it:
 * *You're going!*, *Verify your email* and *Confirm your new email address*.
 *
 * **`bg-success-subtle`, not `bg-success-bg`.** The preset registers `success` with `DEFAULT`,
 * `foreground` and `subtle` and no `bg` key, so `bg-success-bg` compiles to nothing.
 *
 * The heading is the page's `<h1>`: the card is the screen. Anything a caller needs under the action
 * (the reservation's confirmation number) goes in `children`.
 */
import { Check } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

export interface ConfirmationCardProps {
  readonly heading: React.ReactNode;
  readonly lead: React.ReactNode;
  readonly action: { readonly to: string; readonly label: string };
  /**
   * Announce the sentence to assistive technology. On for a confirmation that replaces a form on
   * the same screen (the member pressed something and the answer arrived), off for a page that
   * opens on it.
   */
  readonly announce?: boolean;
  readonly children?: React.ReactNode;
}

export function ConfirmationCard({
  heading,
  lead,
  action,
  announce = false,
  children,
}: ConfirmationCardProps): React.ReactElement {
  return (
    <header className="rounded-xl border border-border-subtle bg-card px-6 py-9 text-center shadow-sm sm:px-8 sm:py-11">
      <span
        className="mx-auto mb-4 inline-flex h-[72px] w-[72px] items-center justify-center rounded-pill bg-success-subtle text-success"
        aria-hidden
      >
        <Check className="h-9 w-9" />
      </span>
      <h1 className="font-brand text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
        {heading}
      </h1>
      <p
        role={announce ? 'status' : undefined}
        className="mx-auto mb-6 mt-2 max-w-[420px] text-md text-muted-foreground"
      >
        {lead}
      </p>
      <Link
        to={action.to}
        className="inline-flex items-center justify-center rounded-pill bg-spark px-6 py-[15px] font-brand text-md font-bold text-white shadow-accent"
      >
        {action.label}
      </Link>
      {children}
    </header>
  );
}
